import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { graphql } from 'react-apollo';
import { mapProps } from 'recompose';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';

import Placeholder from '../PhotoSearchUpgradeCheckout/Placeholder';
import { AccountPhotoSearchCheckout } from '../../../../../graphql/queries/account';
import getPhotoSearchCheckoutSearchItems from '../PhotoSearchCheckout/graphql/queries/getPhotoSearchCheckoutSearchItems';
import {
  isEligibleForShutterbugFreeTrial,
  PAYMENT_PROCESSOR,
} from '../PhotoSearchCheckout/helpers';
import usePhotoSearchCheckout from '../PhotoSearchCheckout/hooks/usePhotoSearchCheckout';
import { getErrorString } from '../../../../../util/normalize-errors';
import {
  openAlert as openAlertAction,
  openModal as openModalAction,
  closeModal as closeModalAction,
} from '../../../../../actions/modal-actions';
import PhotoSearchUpgradeIosNotice from '../PhotoSearchUpgradeCheckout/PhotoSearchUpgradeIOSNotice';
import {
  PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL,
  PhotoSearchCheckoutSuccessModal,
} from '../PhotoSearchCheckout/components/PhotoSearchCheckoutSuccess';
import BoxedItem from '../../../../../components/global/BoxedItem';
import Button from '../../../../../components/global/Button';
import useBasename from '../../../../../components/routing/useBasename';
import CreateWizardLayout from '../../../../pro/components/CreateWizardLayout';
import PhotoSearchUpsellSubscriptionSelection from './components/PhotoSearchUpsellSubscriptionSelection';
import PhotoSearchUpsellInterstitial from './components/PhotoSearchUpsellInterstitial';
import { BASENAME } from '../../../../../constants/basenames';
import usePhotoSearchImmediateCheckout from '../PhotoSearchCheckout/hooks/usePhotoSearchImmediateCheckout';
import CloseIcon from '../../../../../components/icons/CloseIcon';

const propTypes = {
  className: PropTypes.string,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  searchSubscriptionItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      plan: PropTypes.string.isRequired,
      days: PropTypes.number.isRequired,
      price: PropTypes.shape({
        value: PropTypes.number.isRequired,
      }).isRequired,
    }),
  ),
  openAlert: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  refetchAccountSearchSubscriptions: PropTypes.func.isRequired,
  nextUrl: PropTypes.string,
  theme: PropTypes.oneOf(['white']),
  skippable: PropTypes.bool,
  bubbleCopy: PropTypes.shape({
    defaultText: PropTypes.string,
    freeTrialText: PropTypes.string,
  }),
  dismissible: PropTypes.bool,
};

// TODO: We need to make tests for these components. They are key to our business.
// They pretty much contain the same logic as PhotoSearchCheckout, which is tested,
// But needs testing nevertheless

const PhotoSearchUpsell = ({
  account,
  className,
  searchSubscriptionItems,
  openAlert,
  openModal,
  closeModal,
  refetchAccountSearchSubscriptions,
  onClose,
  nextUrl,
  theme,
  skippable,
  bubbleCopy = {},
  dismissible = true,
}) => {
  const classes = classNames({
    'photo-search-upsell': true,
    'p-0': true,
    [`photo-search-upsell-${theme}`]: theme,
    [className]: !!className,
  });
  const basename = useBasename();
  const [interstitialSkipped, setInterstitialSkipped] = useState(
    !isEligibleForShutterbugFreeTrial(account),
  );

  const [selectedSearchItem, setSelectedSearchItem] = useState(null);
  const { onCheckout, submitting } = usePhotoSearchCheckout({
    account,
    selectedSearchItem,
    searchSubscriptionItems,
    onCheckoutError: error => {
      openAlert({
        message: getErrorString(error),
        info: error?.request_id || '',
      });
    },
    onCheckoutSuccess: async () => {
      await refetchAccountSearchSubscriptions();
      openModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL, {
        content: (
          <div className="text-center">
            <p className="text-bold mb-3">Thanks for your purchase!</p>

            <BoxedItem className="photo-search-checkout-success-create-box">
              <p className="text-bold">
                Start sharing photos with friends, family, and your community!
              </p>
              <Button
                onClick={() => {
                  closeModal(PHOTO_SEARCH_CHECKOUT_SUCCESS_MODAL);
                }}
                to={
                  nextUrl ??
                  (basename === BASENAME.SHUTTERBUG
                    ? `/app/${basename}/new`
                    : `/app/${basename}/albums/create`)
                }
                btnStyle="yellow"
                btnSize="lg"
                className="text-black"
              >
                Waldo&ndash;fy your 1st Event!
              </Button>
            </BoxedItem>
          </div>
        ),
        onDismiss: () => {},
      });

      onClose?.({ success: true });
    },
  });

  // If the account meets certain criteria (i.e. has enough search credits, or a
  // subscription, etc) we skip the checkout UI and immediately onboard them
  // by calling the `onCheckout` method manually.
  const skipping = usePhotoSearchImmediateCheckout({
    // We want to avoid this from skipping when it doesn't make sense
    // But need to render the hook due to hook rules.
    account: skippable ? account : null,
    onCheckout,
  });

  if (!account || !searchSubscriptionItems || skipping) {
    return <Placeholder />;
  }

  const [activeSearchSubscription] = account.search_subscriptions.records;

  return (
    <div className={classes}>
      {dismissible && (
        <Button
          btnStyle="link"
          btnSize="lg"
          className="photo-search-upsell-close"
          onClick={onClose}
        >
          <CloseIcon />
        </Button>
      )}

      <h2 className="text-center">
        {interstitialSkipped ? 'Pick a plan' : 'Live the Moment!'}
      </h2>
      {!interstitialSkipped &&
        (bubbleCopy?.defaultText ? (
          <CreateWizardLayout.WaldoBubble
            activeStep={{
              title: isEligibleForShutterbugFreeTrial(account)
                ? bubbleCopy.freeTrialText
                : bubbleCopy.defaultText,
            }}
            position="right"
            icon="/waldo-excited.png"
          />
        ) : (
          <CreateWizardLayout.WaldoBubble
            activeStep={{
              title: isEligibleForShutterbugFreeTrial(account)
                ? 'Start your your free trial below and get matched photos delivered to you!'
                : 'Start creating events now!',
            }}
            position="right"
            icon="/waldo-excited.png"
          />
        ))}
      {/* eslint-disable-next-line no-nested-ternary */}
      {activeSearchSubscription?.payment_processor ===
      PAYMENT_PROCESSOR.APP_STORE ? (
        <PhotoSearchUpgradeIosNotice className="mt-3 ios-notice" />
      ) : interstitialSkipped ? (
        <PhotoSearchUpsellSubscriptionSelection
          className="mt-1"
          searchSubscriptionItems={searchSubscriptionItems}
          onSelect={setSelectedSearchItem}
          selectedSearchItem={selectedSearchItem}
          onCheckout={onCheckout}
          account={account}
          submitting={submitting}
          activeSearchSubscription={activeSearchSubscription}
        />
      ) : (
        <PhotoSearchUpsellInterstitial
          onDismiss={() => setInterstitialSkipped(true)}
          account={account}
        />
      )}
      <PhotoSearchCheckoutSuccessModal />
    </div>
  );
};

PhotoSearchUpsell.propTypes = propTypes;

const withAccount = graphql(AccountPhotoSearchCheckout, {
  props: ({ data: { me, refetch } = {} }) => ({
    account: me,
    refetchAccountSearchSubscriptions: refetch,
  }),
  options: {
    fetchPolicy: 'cache-and-network',
  },
});

const mapDispatchToProps = {
  openAlert: openAlertAction,
  openModal: openModalAction,
  closeModal: closeModalAction,
};

export { PhotoSearchUpsell as PhotoSearchUpsellComponent };

export default compose(
  connect(null, mapDispatchToProps),
  withAccount,
  getPhotoSearchCheckoutSearchItems,
  mapProps(({ data, ...props }) => ({
    ...props,
    searchSubscriptionItems: data?.searchSubscriptionItems,
  })),
)(PhotoSearchUpsell);
